import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '../lib/api';
import EventCard, { type PortalEvent } from '../components/EventCard';
import CategoryTabs from '../components/CategoryTabs';
import SearchBar from '../components/SearchBar';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';
import { IconCalendar, IconSpinner } from '../components/Icons';
import { Screen, EmptyState, btnSoft } from '../components/ui';

interface EventsData {
  events: PortalEvent[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

const SORTS = [
  { value: 'date', label: '活動日期' },
  { value: 'deadline', label: '截止日' },
  { value: 'popular', label: '最多人報' },
  { value: 'newest', label: '最新' },
];

export default function EventListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<EventsData | null>(null);
  const [categories, setCategories] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const category = searchParams.get('category') || 'all';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || 'date';
  const focusSearch = searchParams.get('focus') === 'search';

  useEffect(() => {
    api.getPortalCategories().then((response) => {
      if (response.success && response.data) {
        const map: Record<string, number> = {};
        response.data.forEach((c: any) => { map[c.id] = c.count; });
        setCategories(map);
      }
    });
  }, []);

  useEffect(() => { loadEvents(1); }, [category, search, sort]);

  async function loadEvents(page: number) {
    if (page === 1) setLoading(true); else setLoadingMore(true);
    const response = await api.getPortalEvents({
      category: category !== 'all' ? category : undefined,
      search: search || undefined,
      sort,
      page: page.toString(),
    });
    if (response.success && response.data) {
      if (page === 1) setData(response.data);
      else setData((prev) => ({ ...response.data!, events: [...(prev?.events || []), ...response.data!.events] }));
    }
    setLoading(false);
    setLoadingMore(false);
  }

  function setParam(key: string, value: string | null) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value); else params.delete(key);
    params.delete('focus');
    setSearchParams(params, { replace: true });
  }

  const hasMore = data ? data.pagination.page < data.pagination.totalPages : false;

  return (
    <Screen className="pb-28">
      <div className="safe-top sticky top-0 z-30 bg-white flex flex-col gap-3 px-5 pt-4 pb-3 border-b border-line-soft">
        <h1 className="text-xl font-black">活動</h1>
        <SearchBar
          key={search}
          initial={search}
          autoFocus={focusSearch}
          onSearch={(q) => setParam('search', q || null)}
        />
        <CategoryTabs categories={categories} selected={category} onChange={(c) => setParam('category', c === 'all' ? null : c)} />
      </div>

      <div className="flex flex-col gap-3 px-5 pt-4">
        <div className="flex items-center justify-between">
          <span className="text-[13px] text-muted tabular">
            {loading ? '' : search ? `「${search}」${data?.pagination.total ?? 0} 個結果` : `共 ${data?.pagination.total ?? 0} 個活動`}
          </span>
          <label className="flex items-center gap-1.5 text-[13px] text-muted">
            排序
            <select
              value={sort}
              onChange={(e) => setParam('sort', e.target.value)}
              aria-label="排序方式"
              className="h-8 rounded-full bg-soft px-3 text-[13px] font-bold text-ink border-0"
            >
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : data && data.events.length > 0 ? (
          <>
            {data.events.map((event) => <EventCard key={event.id} event={event} variant="row" />)}
            {hasMore && (
              <button
                type="button"
                onClick={() => loadEvents(data.pagination.page + 1)}
                disabled={loadingMore}
                className={clsx(btnSoft, 'mt-2')}
              >
                {loadingMore ? <IconSpinner size={18} /> : null}
                {loadingMore ? '載入中' : '載入更多'}
              </button>
            )}
          </>
        ) : (
          <EmptyState
            icon={<IconCalendar size={28} />}
            title={search ? '找不到符合的活動' : '目前沒有活動'}
            hint={search ? '換個關鍵字試試，或清除搜尋。' : '新活動開放時會在 LINE 通知。'}
            action={search ? <button type="button" onClick={() => setParam('search', null)} className={btnSoft}>清除搜尋</button> : undefined}
          />
        )}
      </div>

      <BottomNav active="events" />
    </Screen>
  );
}
